import * as React from 'react'
import styled from 'styled-typed'
import { Switch, Route, Redirect, useParams } from 'react-router-dom'
import { History, Location } from 'history'
import { connect } from 'react-redux'
import { State } from 'store'
import { Account } from 'auth/state'
import { BaseRouteParams, withNavigation } from 'hocs'
import { match as RouteMatch } from 'react-router-dom'
import { Navigation } from 'navigation'
import { AccountSettingsService } from 'settings/settingsService'
import { ArticleService } from 'admin/articleService'
import EmailNotificationsPage from 'settings/notifications/emailNotifications'
import NotificationsFeedSection from './notificationsFeed'
import { Container, ContainerBody, Section } from 'uiComponents/settingsContainer'
import { UnderlineNavigationTabs } from 'uiComponents/navigation/underlineNavigationTabs'
import InSectionTabsSlot from 'uiComponents/navigation/sectionTabsSlot'
import { Checkbox } from 'uiComponents/input'
import { withFeatures } from 'features'
import { useMessages } from 'messagesContext'
import { useTranslation } from 'translations'

const AllPartnersToggle = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: 1rem;
    span {
        margin-left: 0.2em;
        font-size: 0.75em;
    }
`

interface NotificationsPageProps {
    history: History
    location: Location
    accountSettingsService: AccountSettingsService
    articleService: ArticleService
    accounts: Account[]
    navigation: Navigation
    match: RouteMatch<{}>
    isAdmin: boolean
    hasFeature: (feature: string, accountSlug?: string) => boolean
}

function NotificationsPage(props: NotificationsPageProps) {
    const [activeSection, setActiveSection] = React.useState<string>('feed')
    const [allPartners, setAllPartners] = React.useState<boolean>(false)
    const { replaceMessages, removeAllMessages } = useMessages()
    const { accountSlug } = useParams<BaseRouteParams>()
    const { t } = useTranslation()

    React.useEffect(() => {
        if (!allPartners && !!props.navigation.query().showAllPartners) {
            setAllPartners(true)
        }
    }, [props.navigation.query().showAllPartners])

    const onSectionChange = (section: string) => {
        setActiveSection(section)
    }

    const onAllPartnersChange = () => {
        props.navigation.addQueryWithReplace({
            showAllPartners: allPartners ? null : 'true',
        })
        setAllPartners(!allPartners)
    }

    return (
        <div style={{ position: 'relative' }}>
            <InSectionTabsSlot>
                <UnderlineNavigationTabs
                    tabs={[
                        {
                            name: t('settings.notifications.tabs.notifications'),
                            current: activeSection === 'feed',
                            href: `/account/${accountSlug}/notifications/feed`,
                            onClick: () => props.history.push(`/account/${accountSlug}/notifications/feed`),
                        },
                        {
                            name: t('settings.notifications.tabs.emailSettings'),
                            current: activeSection === 'settings',
                            href: `/account/${accountSlug}/notifications/settings/`,
                            onClick: () => props.history.push(`/account/${accountSlug}/notifications/settings/`),
                        },
                    ]}
                />
            </InSectionTabsSlot>
            {props.isAdmin && activeSection === 'feed' && (
                <AllPartnersToggle>
                    <Checkbox checked={allPartners} onChange={onAllPartnersChange} />
                    <span>{t('settings.notifications.showAllPartners')}</span>
                </AllPartnersToggle>
            )}
            <Container>
                <ContainerBody>
                    <Switch location={props.location}>
                        <Route
                            path="/account/:accountSlug/notifications/settings"
                            render={() => (
                                <Section>
                                    <EmailNotificationsPage
                                        settingsService={props.accountSettingsService}
                                        articleService={props.articleService}
                                        accountSlug={accountSlug}
                                        onSectionChange={onSectionChange}
                                        replaceMessages={replaceMessages}
                                        removeAllMessages={removeAllMessages}
                                    />
                                </Section>
                            )}
                        />
                        <Route
                            path="/account/:accountSlug/notifications/feed"
                            render={() => (
                                <Section>
                                    <NotificationsFeedSection
                                        accountSlug={accountSlug}
                                        allPartners={allPartners}
                                        isAdmin={props.isAdmin}
                                        accounts={props.accounts}
                                        onSectionChange={onSectionChange}
                                        replaceMessages={replaceMessages}
                                        removeAllMessages={removeAllMessages}
                                    />
                                </Section>
                            )}
                        />
                        <Route render={() => <Redirect to={`/account/${accountSlug}/notifications/feed`} />} />
                    </Switch>
                </ContainerBody>
            </Container>
        </div>
    )
}

const mapStateToProps = (state: State) => {
    return {
        accounts: state.auth.user ? state.auth.user.accounts : [],
        isAdmin: !!state.auth.user?.isAdmin,
    }
}

export default withFeatures(withNavigation(connect(mapStateToProps)(NotificationsPage)))
